import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { getGetDepartmentQueryKey, getGetListsQueryKey, useCreateList } from '@vlabs/api/artemy/client';
import type { CreateListRequest } from '@vlabs/api/artemy/model';
import { PlusIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button/Button';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useCreateDepartmentListsTranslation } from './i18n';
import type { CreateDepartmentListsProps } from './types';
import { ListForm } from '../../../entities/lists';

type CreateDepartmentListButtonProps = CreateDepartmentListsProps & Omit<ButtonProps, 'onClick'>;

const displayName = 'CreateDepartmentListsButton';
const testid = buildTestid(displayName);

function CreateDepartmentListsButton({
  departmentId,
  'data-testid': dataTestId = undefined,
  ...props
}: CreateDepartmentListButtonProps): React.JSX.Element {
  const { t } = useCreateDepartmentListsTranslation();
  const createDepartmentListModal = useModal({ size: 'small' });
  const queryClient = useQueryClient();

  const createList = useCreateList();

  const onSubmit = async ({ user_data }: { user_data?: string }): Promise<void> => {
    const data: CreateListRequest = {
      user_data,
      is_active: true,
      owner_department: departmentId,
      is_open: false,
    };

    try {
      await createList.mutateAsync({ data });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetDepartmentQueryKey(departmentId) }),
        queryClient.invalidateQueries({ queryKey: getGetListsQueryKey() }),
      ]);

      toast.success(t('toast_success'));
      createDepartmentListModal.close();
    } catch {
      toast.error(t('toast_error'));
    }
  };

  return (
    <React.Fragment>
      {createDepartmentListModal.wrap(<ListForm
        context="create"
        onCancel={createDepartmentListModal.close}
        onSubmit={onSubmit}
      />)}

      <Button
        icon={<PlusIcon />}
        onClick={createDepartmentListModal.open}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      >
        {t('title')}
      </Button>
    </React.Fragment>
  );
}

CreateDepartmentListsButton.displayName = displayName;

export { CreateDepartmentListsButton };
